interface TokenResponse {
  success: boolean;
  data?: {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    expires_at?: string;
    user?: {
      id: string;
      phone: string;
      role: string;
    };
  };
  error?: string;
}

interface DecodedToken {
  exp?: number;
  iat?: number;
  userId?: string;
  email?: string;
  role?: string;
}

class AdminTokenClient {
  private readonly authServiceUrl = this.getAuthServiceUrl();
  private isGenerating = false; // Флаг для предотвращения одновременных запросов
  private lastRequestTime = 0; // Время последнего запроса для rate limiting
  
  // Кеширование активных refresh запросов для предотвращения множественных одновременных запросов
  private activeRefreshPromise: Promise<TokenResponse> | null = null;
  private refreshCacheKey: string | null = null;

  private getAuthServiceUrl(): string {
    // В браузере всегда используем относительный путь через прокси
    if (typeof window !== 'undefined') {
      return '/api/proxy/auth';
    }
    
    // На сервере используем прямые URL
    if (import.meta.env?.VITE_AUTH_SERVICE_URL) {
      return import.meta.env.VITE_AUTH_SERVICE_URL;
    }
    
    const isDevelopment = import.meta.env.DEV;
    
    if (isDevelopment) {
      return 'http://localhost:3001'; // auth-service по умолчанию
    }
    
    // В production должен быть установлен VITE_AUTH_SERVICE_URL
    return 'http://auth-service:3001';
  }

  /**
   * Генерация JWT токена для админа
   * Использует userId и email для поиска пользователя
   */
  async generateAdminToken(userId: string, email?: string): Promise<TokenResponse> {
    try {
      const url = `${this.authServiceUrl}/api/v1/auth/admin-token`;
      console.log('🔐 Генерация JWT токена для админа:', { url, userId, email });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          email,
          role: 'admin'
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('❌ [Admin Token Client] Ошибка ответа:', result);
        throw new Error(result.error || `HTTP ${response.status}`);
      }

      // Преобразуем ответ auth-service в наш формат
      // auth-service возвращает access_token напрямую, а не в data
      if (result.success) {
        return {
          success: true,
          data: {
            access_token: result.access_token || result.data?.access_token,
            refresh_token: result.refresh_token || result.data?.refresh_token,
            expires_in: result.expires_in || result.data?.expires_in || 3600,
            expires_at: result.expires_at || result.data?.expires_at,
            user: result.user || result.data?.user
          }
        };
      }

      console.error('❌ [Admin Token Client] Неуспешный ответ:', result);
      return result;
    } catch (error) {
      console.error('❌ Ошибка генерации токена для админа:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Неизвестная ошибка'
      };
    }
  }

  async generateAdminToken(): Promise<TokenResponse> {
    // Для админов используется стандартная аутентификация через SMS
    // Этот метод больше не используется напрямую
    // Вместо этого админы должны использовать send-code и verify-code
    console.warn('generateAdminToken: Используйте стандартную аутентификацию через SMS');
    return {
      success: false,
      error: 'Используйте стандартную аутентификацию через SMS (send-code/verify-code)'
    };
  }

  async refreshAdminToken(refreshToken: string): Promise<TokenResponse> {
    // Кеширование: если уже выполняется refresh с тем же токеном, используем существующий промис
    const cacheKey = refreshToken.substring(0, 20); // Первые 20 символов для идентификации
    
    if (this.activeRefreshPromise && this.refreshCacheKey === cacheKey) {
      console.log('🔄 Используем кешированный refresh запрос (предотвращение множественных запросов)');
      return this.activeRefreshPromise;
    }
    
    // Создаем новый промис для refresh
    this.refreshCacheKey = cacheKey;
    this.activeRefreshPromise = this._doRefreshToken(refreshToken);
    
    // Очищаем кеш после завершения (через 5 секунд)
    this.activeRefreshPromise.finally(() => {
      setTimeout(() => {
        this.activeRefreshPromise = null;
        this.refreshCacheKey = null;
      }, 5000);
    });
    
    return this.activeRefreshPromise;
  }
  
  private async _doRefreshToken(refreshToken: string): Promise<TokenResponse> {
    try {
      // Используем эндпоинт для админов
      const url = `${this.authServiceUrl}/api/v1/admin/refresh`;
      console.log('🔄 Обновление токена администратора:', { url, authServiceUrl: this.authServiceUrl });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Слишком частые запросы. Подождите 1 минуту.');
        }
        throw new Error(result.error || `HTTP ${response.status}`);
      }

      // Преобразуем ответ auth-service в наш формат
      // auth-service возвращает access_token и refresh_token напрямую, а не в data
      if (result.success) {
        return {
          success: true,
          data: {
            access_token: result.access_token || result.data?.access_token,
            refresh_token: result.refresh_token || result.data?.refresh_token,
            expires_in: result.expires_in || result.data?.expires_in,
            expires_at: result.expires_at || result.data?.expires_at,
            user: result.user || result.data?.user || result.admin ? {
              id: result.admin.id,
              phone: result.admin.email,
              role: result.admin.role
            } : undefined
          }
        };
      }

      return result;
    } catch (error) {
      console.error('Ошибка обновления токена:', error);
      // Очищаем кеш при ошибке
      this.activeRefreshPromise = null;
      this.refreshCacheKey = null;
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Неизвестная ошибка'
      };
    }
  }

  // Автоматическая установка токена в localStorage
  // Для админов используется стандартная аутентификация через SMS
  // Этот метод больше не генерирует токены автоматически
  async generateAndInstallToken(): Promise<boolean> {
    console.warn('generateAndInstallToken: Для админов используется стандартная аутентификация через SMS');
    
    // Проверяем, есть ли уже сохраненный токен
    const existingToken = localStorage.getItem('admin-token') || localStorage.getItem('auth-token');
    if (existingToken) {
      console.log('✅ Токен уже существует');
      return true;
    }
    
    return false;
  }

  // Проверка и автообновление токена
  async ensureValidToken(): Promise<string | null> {
    try {
      // Проверяем, что мы в браузере
      if (typeof window === 'undefined') {
        console.warn('⚠️ ensureValidToken вызван на сервере, localStorage недоступен');
        return null;
      }
      
      const token = localStorage.getItem('admin-token') || localStorage.getItem('auth-token');
      const refreshToken = localStorage.getItem('refresh-token') || localStorage.getItem('admin_refresh_token');

      // Если нет токена - возвращаем null (админ должен авторизоваться через SMS)
      if (!token) {
        console.log('🔄 Токен отсутствует, требуется авторизация');
        console.log('   📋 Проверка localStorage:', {
          'admin-token': localStorage.getItem('admin-token') ? 'есть' : 'нет',
          'auth-token': localStorage.getItem('auth-token') ? 'есть' : 'нет',
          'refresh-token': localStorage.getItem('refresh-token') ? 'есть' : 'нет',
          'admin_refresh_token': localStorage.getItem('admin_refresh_token') ? 'есть' : 'нет',
        });
        return null;
      }
      
      // Проверяем срок действия токена
      try {
        // Декодируем токен без проверки подписи (только для получения exp)
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded: DecodedToken = JSON.parse(jsonPayload);
        
        const now = Math.floor(Date.now() / 1000);
        const expiresAt = decoded.exp || 0;
        const timeUntilExpiry = expiresAt - now;
        
        // Если токен уже истек или истечет менее чем через 10 минут - обновляем
        // Увеличен порог для уменьшения частоты обновлений (при токене 4 часа это ~4% от времени жизни)
        const REFRESH_THRESHOLD = 10 * 60; // 10 минут в секундах
        
        if (timeUntilExpiry < REFRESH_THRESHOLD) {
          if (timeUntilExpiry <= 0) {
            console.log('⚠️ Токен истек, обновляю через refresh token...');
          } else {
            console.log(`🔄 Токен истечет через ${Math.floor(timeUntilExpiry / 60)} минут, обновляю заранее...`);
          }
          
          // Пытаемся обновить через refresh token
          if (refreshToken) {
            const result = await this.refreshAdminToken(refreshToken);
            
            if (result.success && result.data) {
              // Сохраняем новые токены
              localStorage.setItem('admin-token', result.data.access_token);
              localStorage.setItem('auth-token', result.data.access_token);
              localStorage.setItem('admin_access_token', result.data.access_token);
              
              if (result.data.refresh_token) {
                localStorage.setItem('refresh-token', result.data.refresh_token);
                localStorage.setItem('admin_refresh_token', result.data.refresh_token);
              }
              
              console.log('✅ Токен успешно обновлен через refresh token');
              return result.data.access_token;
            } else {
              console.error('❌ Не удалось обновить токен:', result.error);
              // Если refresh token невалиден, очищаем все токены
              if (result.error?.includes('invalid') || result.error?.includes('expired')) {
                console.warn('⚠️ Refresh token невалиден, требуется повторная авторизация');
                localStorage.removeItem('admin-token');
                localStorage.removeItem('auth-token');
                localStorage.removeItem('admin_access_token');
                localStorage.removeItem('refresh-token');
                localStorage.removeItem('admin_refresh_token');
                return null;
              }
            }
          } else {
            console.warn('⚠️ Refresh token отсутствует, невозможно обновить токен');
            // Если токен истек и нет refresh token, очищаем
            if (timeUntilExpiry <= 0) {
              localStorage.removeItem('admin-token');
              localStorage.removeItem('auth-token');
              localStorage.removeItem('admin_access_token');
              return null;
            }
          }
        } else {
          // Токен валиден и не скоро истечет
          const minutesLeft = Math.floor(timeUntilExpiry / 60);
          if (minutesLeft > 10) {
            // Логируем только если токен валиден более 10 минут (чтобы не спамить)
            console.log(`✅ Токен валиден еще ${minutesLeft} минут`);
          }
        }

        if (timeUntilExpiry <= 0) {
          return null;
        }

        return token;
      } catch (decodeError) {
        console.error('❌ Ошибка декодирования токена:', decodeError);
        // Если токен невалидный формат, пытаемся обновить через refresh token
        if (refreshToken) {
          console.log('🔄 Токен имеет невалидный формат, пытаюсь обновить через refresh token...');
          const result = await this.refreshAdminToken(refreshToken);
          
          if (result.success && result.data) {
            localStorage.setItem('admin-token', result.data.access_token);
            localStorage.setItem('auth-token', result.data.access_token);
            localStorage.setItem('admin_access_token', result.data.access_token);
            if (result.data.refresh_token) {
              localStorage.setItem('refresh-token', result.data.refresh_token);
              localStorage.setItem('admin_refresh_token', result.data.refresh_token);
            }
            return result.data.access_token;
          }
        }
        
        // Если не удалось обновить, очищаем токены
        localStorage.removeItem('admin-token');
        localStorage.removeItem('auth-token');
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('refresh-token');
        localStorage.removeItem('admin_refresh_token');
        return null;
      }
    } catch (error) {
      console.error('❌ Ошибка обеспечения валидности токена:', error);
      return null;
    }
  }
}

// Экспортируем singleton
export const adminTokenClient = new AdminTokenClient();

// Функция для быстрой установки токена
export async function installAdminToken(): Promise<boolean> {
  return await adminTokenClient.generateAndInstallToken();
}

// Функция для получения валидного токена
export async function getValidAdminToken(): Promise<string | null> {
  return await adminTokenClient.ensureValidToken();
}

// Функция для генерации токена для админа
export async function generateAdminToken(userId: string, email?: string): Promise<TokenResponse> {
  return await adminTokenClient.generateAdminToken(userId, email);
} 